
/**
 * Функция вычисляет основной цвет по уровню сходства (0..1).
 * Например, можно использовать градиент от красного (0) до зелёного (1).
 */
export function colorBySimilarity(similarity: number): string {
    // От 0 (красный) до 1 (зелёный), умножаем на 120 по шкале HSL
    const hue = (1 - similarity) * 120;
    return `hsl(${hue}, 100%, 50%)`;
}

/**
 * Функция, вычисляющая цвет, когда нам надо показать
 * "средний" результат для двух значений сходства.
 */
export function averageColorBySimilarity(simA: number, simB: number): string {
    const hueA = (1 - simA) * 120;
    const hueB = (1 - simB) * 120;
    const avgHue = (hueA + hueB) / 2;
    return `hsl(${avgHue}, 100%, 50%)`;
}

/**
 * Подбираем цвет текста (белый или чёрный) для лучшей контрастности,
 * исходя из сходства. Если «сходство» большое → фон более зелёный → текст можно сделать тёмным.
 */
export function textColorBySimilarity(similarity: number): string {
    // Вычисляем hue так же, как в colorBySimilarity
    const hue = (1 - similarity) * 120;
    const saturation = 100; // %
    const lightness = 50;   // %

    // Преобразование HSL в RGB
    const s = saturation / 100;
    const l = lightness / 100;
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const hPrime = hue / 60;
    const x = c * (1 - Math.abs((hPrime % 2) - 1));
    let r1 = 0, g1 = 0, b1 = 0;

    if (hPrime >= 0 && hPrime < 1) {
        r1 = c; g1 = x; b1 = 0;
    } else if (hPrime >= 1 && hPrime < 2) {
        r1 = x; g1 = c; b1 = 0;
    } else if (hPrime >= 2 && hPrime < 3) {
        r1 = 0; g1 = c; b1 = x;
    } else if (hPrime >= 3 && hPrime < 4) {
        r1 = 0; g1 = x; b1 = c;
    } else if (hPrime >= 4 && hPrime < 5) {
        r1 = x; g1 = 0; b1 = c;
    } else if (hPrime >= 5 && hPrime < 6) {
        r1 = c; g1 = 0; b1 = x;
    }

    const m = l - c / 2;
    const r = (r1 + m) * 255;
    const g = (g1 + m) * 255;
    const b = (b1 + m) * 255;

    // Вычисляем яркость
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;

    // Если яркость достаточно высокая, используем чёрный текст, иначе — белый
    return brightness > 128 ? 'black' : 'white';
}

/**
 * Средняя схожесть для пары sim = [simA, simB].
 */
export function averageSim([simA, simB]: [number, number]): number {
    return (simA + simB) / 2;
}

/**
 * Взвешенная схожесть: weightA ∈ [0,1], weightB = 1 − weightA.
 */
export function weightedSim([simA, simB]: [number, number], weightA: number): number {
    const wA = Math.max(0, Math.min(1, weightA));
    return simA * wA + simB * (1 - wA);
}
